import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import { getKsdmConfig } from 'helpers/index';

/**
 * CMS Admin (`/admin/`, Phase 7) — the ONE RTK Query API every CMS module
 * extends. Phase 7 run 1 (shell) ships the base; each later tranche adds a
 * `<module>Api.ts` that calls `cmsApi.injectEndpoints({...})`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ D-1 — ONE `createApi`, `injectEndpoints` PER MODULE.
 *
 * The goal doc assumed `api/api.js` (1,483 LOC) was the data layer that had to
 * be mapped onto slices. It is NOT — measured 2026-08-05, it is the *screen
 * registry* and makes zero HTTP calls (see `frontend/PHASE7_CMS_SURVEY.md` §0).
 * The real data layer is 436 distinct endpoints consumed as flat Backbone
 * collections, ~90% under `/api/v1.0/cms/<mfg>/v1.0.0/ahu/<family>/` and the
 * rest under `/api/v1.0/master/v1.0.0/ahu/<family>/`.
 *
 * 31 modules cannot all edit one endpoints object without colliding, and 31
 * separate `createApi`s would fragment the cache tags. `injectEndpoints` gives
 * both: one cache + one middleware entry in the store, one new FILE per tranche.
 *
 * ★★ BUT ONE FILE PER TRANCHE DOES NOT PREVENT **NAME** COLLISIONS — and a
 * collision here is SILENT AND ORDER-DEPENDENT. Learned the hard way in T5.
 *
 * `injectEndpoints` defaults to `overrideExisting: false`, so if two modules
 * define the same endpoint name, **the FIRST injection wins and the second is
 * dropped**. Screens are lazy-loaded, so "first" means *whichever screen the
 * user happened to open first* — the bug is invisible until two specific screens
 * are visited in one session, in one order.
 *
 * MEASURED 2026-08-09: `networkApi` (T2) and `permissionsApi` (T5) both defined
 * `getUsers`, `getCompanies` and `patchUser`. Visiting Mfg Network and THEN
 * Permissions ▸ Access rendered Access completely EMPTY (0 chars vs 9,673) with
 * `TypeError: Cannot destructure property 'companyId' of 'undefined'` — because
 * Access was calling Network's endpoint with Access's arguments. Both screens
 * had already passed independent verification; neither verifier saw it, because
 * each opened only its own screen. It surfaced in an all-screens smoke walk.
 *
 * ⇒ **RULE FOR EVERY REMAINING TRANCHE: prefix your endpoint names with your
 * module** (`permGetUsers`, not `getUsers`) unless you are deliberately reusing
 * another module's endpoint. `assertNoCmsEndpointCollisions()` below fails loudly
 * in dev if you forget.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★ D-2 — THREAD THE REAL MANUFACTURER ID. DO NOT HARD-CODE `1`.
 *
 * ~90% of legacy URLs hard-code `/api/v1.0/cms/1/...`. That is not a convention
 * worth preserving: `django/api/rest_cms/` rejects the request in **107**
 * places with `if int(request.user.mfg.id) != int(pk)`, so the legacy page only
 * works at all for manufacturer 1 (the test user `kinetix` is `mfg_id=1`, which
 * is the only reason nobody has noticed). `cmsUrl()` reads the real id from the
 * server-injected boot config.
 *
 * For mfg 1 the emitted URL is byte-identical to legacy, so this is a
 * NON-REGRESSION, not a behaviour change — and the backend's own guard says it
 * is the intended contract. Recorded in the tracker as a deliberate deviation
 * with reason, per the parity-only rule.
 */

/** Manufacturer id from the boot config (`react_views._mfg_json`). */
export const getCmsMfgId = (): number | string =>
  getKsdmConfig()?.mfg?.id ?? 1;

/** The CMS version segment. Every legacy URL uses `v1.0.0`; none templates it. */
export const CMS_API_VERSION = 'v1.0.0';

/**
 * CMS-scoped URL: `/api/v1.0/cms/<mfg>/v1.0.0/<path>`.
 * `path` is given WITHOUT a leading slash, e.g. `cmsUrl('ahu/dampers/')`.
 */
export const cmsUrl = (path: string): string =>
  `/api/v1.0/cms/${getCmsMfgId()}/${CMS_API_VERSION}/${path}`;

/**
 * Master (KSDM-wide catalog) URL: `/api/v1.0/master/v1.0.0/<path>`.
 * Master rows are shared across manufacturers — treat as read-only unless a
 * tranche has proven otherwise (see the master-PATCH trap in the survey §6.7).
 */
export const masterUrl = (path: string): string =>
  `/api/v1.0/master/${CMS_API_VERSION}/${path}`;

/**
 * Cache tags. Kept in ONE list so 31 tranches share an invalidation vocabulary
 * instead of inventing per-module strings that never invalidate each other.
 * A tranche adds its own tags here when it lands; the shell needs only these.
 */
export const CMS_TAG_TYPES = [
  'CmsProduct',
  'CmsComponentCatalog',
  'CmsAccessoryCatalog',
  /**
   * ★★ ADDED IN T3 — the React stand-in for the ONE legacy dispatcher event that
   * crosses module boundaries.
   *
   * `controller/dispatcher.js` is a bare `_.extend({}, Backbone.Events)`, and
   * exactly two of its events matter across tranches:
   *
   *   `change:cost:data`  published by `cost_codes/view/cost_and_description.js:97,163,230`
   *   `change:task:data`  published by `activities/view/cost_and_description.js:114,200,261`
   *
   * subscribed by `allocations/view/materials.js:55` (cost only) and
   * `allocations/view/tasks.js:59,69` (both), each of which RE-FETCHES its
   * collections and re-renders. T1 shipped `allocations` with NO tags because no
   * publisher existed yet; T3 is that publisher, so the vocabulary lands here.
   *
   * ★ MEASURED END TO END on `:8130` BEFORE wiring anything — the full request
   * stream is in the header of `screens/cost-charts/costChartsApi.ts`. Legacy
   * does refresh, it refreshes immediately at publish time (navigating back to
   * Allocations afterwards emits ZERO requests), and only for sub-views that
   * have been opened at least once, because the listener is created in
   * `initialize`. RTK Query's subscription model reproduces that condition for
   * free: a mounted-but-hidden tab still holds a subscription, a tab that was
   * never opened does not.
   *
   * `CmsCostData` — anything derived from `ahu/cost_codes/`.
   * `CmsTaskData` — anything derived from `ahu/task_codes/`.
   * Providers: `allocationsApi`'s four queries. Invalidators: the three CODE
   * mutations in `costChartsApi`. The GROUPS and GROUPS-AND-CODES mutations
   * publish nothing in legacy and carry no tags here either.
   */
  'CmsCostData',
  'CmsTaskData',
  /**
   * ★★ ADDED IN T7 (Builder 1) — the React stand-in for the ONE cross-module
   * dispatcher event `rawMaterial` publishes.
   *
   * `constructions/rawMaterial/view/finish.js:34` and `finish_item.js:49,60`
   * fire `Dispatcher.trigger('update:coating:list')` on coating add / field
   * change / delete. The ONLY subscribers are `constructions/cabinet/view/
   * casing.js:37` and `floor.js:37`, which re-fetch their coating dropdown.
   *
   * `CmsCoatingList` — the coating (finish_paints) list. INVALIDATOR:
   * `rawMaterial`'s `FinishTab` after every finish write (via
   * `cmsApi.util.invalidateTags`). PROVIDER: cabinet's coating query
   * (`getCabCoatingList` in `screens/constructions/cabinetApi.ts`, which
   * `providesTags:['CmsCoatingList']`). rawMaterial's OWN
   * `getRawMaterialFinishRows` deliberately does NOT provide it — legacy never
   * re-fetches the finish list after a write.
   */
  'CmsCoatingList',
  /**
   * ★ ADDED IN T7 (Builder 1) — the SUBSCRIBER side of a cross-module event.
   * `cnstrSettings/view/cnstrSettings.js:68` does
   * `listenTo(Dispatcher,'update:drain_pan:types', …drainPanTypes.fetch())`, so
   * the drain-pan TYPE list (the `ahu/drain_pan/types/` options behind the Drain
   * Pan multi-select) re-fetches when the Drain Pan accessory page adds/edits a
   * type. PROVIDER: cnstrSettings' `getCnstrDrainPanTypes`. INVALIDATOR: none
   * — a known parity gap. Legacy's Drain Pan views (`drainPan.js`,
   * `drainPanType.js`) fired `update:drain_pan:types` on type
   * add/rename/delete; the T8c port (`accessories/DrainPanScreen.tsx`,
   * `DrainPanType.tsx`) writes via `accPost`/`accPatch`/`accDelete` and never
   * invalidates this tag, so cnstrSettings' list stays stale until reload.
   */
  'CmsDrainPanTypes',
  /**
   * ★ ADDED with the move of Special Options into Construction ▸ Class.
   * `SpecialOptionSettings` is keyed by CONSTRUCTION CLASS id, so each class
   * tab caches its own hundred rows. The only invalidator is Select All /
   * De-Select All, which rewrites every `checkbox` in one class at once and
   * so has to refetch rather than patch state row by row; the per-row PATCHes
   * deliberately provide nothing, because the row already holds what the user
   * just typed and a refetch would fight the cursor.
   */
  'SpecialOptionSettings',
] as const;

export const cmsApi = createApi({
  reducerPath: 'cmsApi',
  baseQuery,
  tagTypes: CMS_TAG_TYPES,
  endpoints: (builder) => ({
    /**
     * The AHU product the CMS product-section nav is built for.
     * `view/main.menu.js:44` instantiates the product menu with a hard-coded
     * `product_id: 1`, and `view/product.menu.js:39-41` fetches
     * `products/model/product.js:getUrlRoot(mfg.id) + product_id`.
     */
    getCmsProduct: builder.query<CmsProduct, number | string>({
      query: (productId) => cmsUrl(`ahu/products/${productId}`),
      providesTags: (_r, _e, id) => [{ type: 'CmsProduct' as const, id }],
    }),

    /**
     * Components enabled for this manufacturer. Drives the `components` nav
     * group (`tmpl/product.menu.tmpl:143`, `{{#each components}}`).
     */
    getCmsComponentCatalog: builder.query<CmsCatalogRow[], void>({
      query: () => cmsUrl('ahu/components/'),
      providesTags: ['CmsComponentCatalog'],
    }),

    /**
     * Accessories enabled for this manufacturer. Drives the `accessories` nav
     * group (`tmpl/product.menu.tmpl:170`, `{{#each accessories}}`).
     */
    getCmsAccessoryCatalog: builder.query<CmsCatalogRow[], void>({
      query: () => cmsUrl('ahu/accessories/'),
      providesTags: ['CmsAccessoryCatalog'],
    }),
  }),
});

/** A row of the components / accessories catalog. */
export interface CmsCatalogRow {
  id?: number;
  /** Machine key — `fan`, `damper`, `uvc_system`, … Drives the nav anchor. */
  value: string;
  /** Display label — `Fan`, `Damper`, `UV-C`, … */
  name: string;
  component_id?: number;
  accessory_id?: number;
}

export interface CmsProduct {
  id: number;
  name?: string;
  [k: string]: unknown;
}

export const {
  useGetCmsProductQuery,
  useGetCmsComponentCatalogQuery,
  useGetCmsAccessoryCatalogQuery,
} = cmsApi;

/**
 * ★★ DEV-TIME GUARD against the silent, order-dependent endpoint-name collision
 * described at the top of this file.
 *
 * Call it at the end of every `<module>Api.ts`, passing the names that module
 * injected. Because `injectEndpoints` keeps the FIRST definition, a duplicate is
 * otherwise dropped in silence and only misbehaves once two particular screens
 * are opened in one particular order — which is exactly how T5 shipped a screen
 * that rendered empty after Mfg Network had been visited.
 *
 * Throws in dev so it is impossible to miss; a no-op in production, where the
 * cost of a hard failure outweighs the (already-shipped) bug.
 */
const injectedCmsEndpoints = new Map<string, string>();

export function assertNoCmsEndpointCollisions(moduleName: string, names: string[]): void {
  if (!import.meta.env?.DEV) return;
  const clashes = names.filter(
    (n) => injectedCmsEndpoints.has(n) && injectedCmsEndpoints.get(n) !== moduleName
  );
  names.forEach((n) => {
    if (!injectedCmsEndpoints.has(n)) injectedCmsEndpoints.set(n, moduleName);
  });
  if (clashes.length) {
    throw new Error(
      `cmsApi endpoint name collision: ${moduleName} re-defines [${clashes.join(', ')}], ` +
        `already injected by ${clashes.map((n) => injectedCmsEndpoints.get(n)).join(', ')}. ` +
        `RTK Query keeps the FIRST definition and silently drops yours, so which one wins ` +
        `depends on the order the user opens screens. Prefix your endpoint names with your ` +
        `module (e.g. "${moduleName}${clashes[0][0].toUpperCase()}${clashes[0].slice(1)}").`
    );
  }
}
